'use client';

import React, { useState } from 'react';
import { WebcamType } from '@/api/region/types';
import { getLang } from '@/i18n/locale';
import { OneTrustCategories } from '@/tracking/onetrust/constants';
import { useOneTrustIframe } from '@/util/useAddOneTrustIframe';

// Consent-gated live-stream embed, layered over the webcam poster photo
// inside ResortWebcam. The iframe is injected by useOneTrustIframe under
// the Targeting Cookies category, so the poster stays visible until the
// visitor has consented and the stream loads.
//
// Feratel streams take the UI language as an extra `lg` query param; the
// provider URLs from the API always carry a query string already, so the
// `&` separator is safe (same contract the legacy FeaturedCamera relied on).

export type LiveWebcamStreamProps = {
  videoStream: string;
  webcamType?: WebcamType;
  /** Accessible title for the injected iframe. */
  title: string;
};

const IFRAME_ATTRIBUTES = {
  class: 'absolute inset-0 h-full w-full border-0',
  allowfullscreen: 'true',
};

export const LiveWebcamStream = ({ videoStream, webcamType, title }: LiveWebcamStreamProps) => {
  // Callback ref via state: useOneTrustIframe only injects once it sees a
  // non-null element, and a plain useRef mutation never re-renders, so the
  // hook would keep seeing null on the first pass.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  let src = videoStream;
  if (webcamType === WebcamType.Feratel) {
    src += `&lg=${getLang()}`;
  }

  useOneTrustIframe(container, src, title, OneTrustCategories.TargetingCookies, IFRAME_ATTRIBUTES);

  return <div ref={setContainer} className="absolute inset-0" />;
};
